// Cosmétiques refaits (choix de Léni, 4 novembre 2026) : chaque pièce du tank prend une couleur libre, une
// matière, un motif et un autocollant ; s'y ajoutent une aura, une traînée et des effets de tir, d'élimination et
// de mort. Les couleurs sont gratuites ; matières, motifs, autocollants et effets se gagnent (coffres en pièces)
// ou s'achètent directement (gemmes). Rien ne change la hitbox, la précision ni la lisibilité des tirs (dossier §9) :
// la couleur du camp reste au cœur de la tourelle, les balles gardent leur couleur, les contours restent sombres.

export type Rarity = 'commun' | 'rare' | 'epique' | 'legendaire' | 'mythique';
export const RARITIES: Rarity[] = ['commun', 'rare', 'epique', 'legendaire', 'mythique'];
export const RARITY_NAME: Record<Rarity, string> = { commun: 'Commun', rare: 'Rare', epique: 'Épique', legendaire: 'Légendaire', mythique: 'Mythique' };
export const RARITY_COLOR: Record<Rarity, number> = {
  commun: 0x9aa6b2,
  rare: 0x4aa3ff,
  epique: 0xb26bff,
  legendaire: 0xffb340,
  mythique: 0xff4f7b,
};

export type PartId = 'hull' | 'treads' | 'turret' | 'barrels';
export const PARTS: PartId[] = ['hull', 'treads', 'turret', 'barrels'];
/**
 * Pièces qui reçoivent un autocollant : les chenilles, seule partie large et bien visible sur les côtés (la caisse est
 * presque entièrement sous le dôme, le dôme garde la couleur du camp, les tubes restent lisibles).
 */
export const DECAL_PARTS: PartId[] = ['treads'];

export type CosmeticSlot = 'mat' | 'pat' | 'dec' | 'aura' | 'trail' | 'shot' | 'kill' | 'death';

export interface CosmeticDef {
  id: string;
  slot: CosmeticSlot;
  name: string;
  rarity: Rarity;
}

const def = (slot: CosmeticSlot, key: string, name: string, rarity: Rarity): CosmeticDef => ({ id: `${slot}:${key}`, slot, name, rarity });

/** Matières : la façon dont la couleur choisie est rendue. « Mat » est gratuite. */
export const MATERIALS = [
  def('mat', 'mat', 'Mat', 'commun'),
  def('mat', 'satin', 'Satin', 'commun'),
  def('mat', 'brosse', 'Métal brossé', 'rare'),
  def('mat', 'carbone', 'Carbone', 'rare'),
  def('mat', 'chrome', 'Chrome', 'epique'),
  def('mat', 'nacre', 'Nacre', 'epique'),
  def('mat', 'or', 'Or', 'legendaire'),
  def('mat', 'neon', 'Néon', 'legendaire'),
  def('mat', 'magma', 'Magma', 'mythique'),
];

export const PATTERNS = [
  def('pat', 'rayures', 'Rayures', 'commun'),
  def('pat', 'damier', 'Damier', 'commun'),
  def('pat', 'camo', 'Camouflage', 'rare'),
  def('pat', 'hexa', 'Hexagones', 'rare'),
  def('pat', 'ecailles', 'Écailles', 'epique'),
  def('pat', 'circuit', 'Circuit', 'epique'),
  def('pat', 'tigre', 'Tigre', 'legendaire'),
  def('pat', 'flammes', 'Flammes', 'legendaire'),
];

export const DECALS = [
  def('dec', 'etoile', 'Étoile', 'commun'),
  def('dec', 'chevrons', 'Chevrons', 'commun'),
  def('dec', 'cible', 'Cible', 'commun'),
  def('dec', 'eclair', 'Éclair', 'rare'),
  def('dec', 'flamme', 'Flamme', 'rare'),
  def('dec', 'griffe', 'Griffes', 'rare'),
  def('dec', 'ailes', 'Ailes', 'epique'),
  def('dec', 'couronne', 'Couronne', 'epique'),
  def('dec', 'crane', 'Crâne', 'legendaire'),
  def('dec', 'dragon', 'Dragon', 'mythique'),
];

export const AURAS = [
  def('aura', 'etincelles', 'Étincelles', 'rare'),
  def('aura', 'flocons', 'Flocons', 'rare'),
  def('aura', 'braises', 'Braises', 'epique'),
  def('aura', 'lucioles', 'Lucioles', 'epique'),
  def('aura', 'orage', 'Orage', 'legendaire'),
  def('aura', 'runes', 'Anneau de runes', 'mythique'),
];

export const TRAILS = [
  def('trail', 'poussiere', 'Poussière', 'commun'),
  def('trail', 'lumiere', 'Sillage lumineux', 'rare'),
  def('trail', 'bulles', 'Bulles', 'rare'),
  def('trail', 'flammes', 'Flammes', 'epique'),
  def('trail', 'etoiles', 'Poussière d’étoiles', 'legendaire'),
  def('trail', 'arcenciel', 'Arc-en-ciel', 'mythique'),
];

export const SHOTS = [
  def('shot', 'etincelles', 'Gerbe d’étincelles', 'commun'),
  def('shot', 'fumee', 'Fumée de poudre', 'rare'),
  def('shot', 'plasma', 'Anneau de plasma', 'epique'),
  def('shot', 'etoile', 'Éclat d’étoile', 'legendaire'),
];

export const KILLS = [
  def('kill', 'confettis', 'Confettis', 'rare'),
  def('kill', 'onde', 'Onde de choc', 'epique'),
  def('kill', 'foudre', 'Foudre', 'legendaire'),
  def('kill', 'supernova', 'Supernova', 'mythique'),
];

export const DEATHS = [
  def('death', 'eclats', 'Éclats de blindage', 'rare'),
  def('death', 'fumee', 'Fumée noire', 'rare'),
  def('death', 'implosion', 'Implosion', 'epique'),
  def('death', 'vaporisation', 'Vaporisation', 'legendaire'),
];

export const COSMETIC_DEFS: CosmeticDef[] = [...MATERIALS, ...PATTERNS, ...DECALS, ...AURAS, ...TRAILS, ...SHOTS, ...KILLS, ...DEATHS];
export const COSMETIC_DEF: Record<string, CosmeticDef> = Object.fromEntries(COSMETIC_DEFS.map((d) => [d.id, d]));
/** Objets possédés par tout le monde dès le départ. */
export const FREE_COSMETICS = new Set(['mat:mat']);

export interface PartPaint {
  /** Couleur libre (0xRRGGBB). */
  color: number;
  /** Matière (identifiant `mat:*`). */
  mat: string;
  /** Motif (`pat:*`) ou aucun, et sa couleur. */
  pat: string | null;
  patColor: number;
  /** Autocollant (`dec:*`) ou aucun, et sa couleur ; seulement sur DECAL_PARTS. */
  dec: string | null;
  decColor: number;
}

export interface TankLook {
  parts: Record<PartId, PartPaint>;
  aura: string | null;
  trail: string | null;
  shot: string | null;
  kill: string | null;
  death: string | null;
}

const plain = (color: number): PartPaint => ({ color, mat: 'mat:mat', pat: null, patColor: 0xb8c2cc, dec: null, decColor: 0xeef3f8 });

/** Apparence par défaut : celle du style Atelier d'aujourd'hui. */
export const DEFAULT_LOOK: TankLook = {
  parts: { hull: plain(0x8b97a5), treads: plain(0x3a414b), turret: plain(0x8b97a5), barrels: plain(0x5f6976) },
  aura: null,
  trail: null,
  shot: null,
  kill: null,
  death: null,
};

const isColor = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 0xffffff;

/**
 * Plancher de luminosité par pièce (canal le plus fort) : un tank peint presque noir se confondrait avec le sol sombre.
 * Les chenilles peuvent être plus sombres (elles le sont par défaut). Une couleur trop sombre est éclaircie en gardant
 * sa teinte.
 */
export const MIN_BRIGHT: Record<PartId, number> = { hull: 0x46, treads: 0x26, turret: 0x46, barrels: 0x3a };
export function brightEnough(color: number, part: PartId): number {
  const r = (color >> 16) & 255;
  const g = (color >> 8) & 255;
  const b = color & 255;
  const m = Math.max(r, g, b);
  const min = MIN_BRIGHT[part];
  if (m >= min) return color;
  if (m === 0) return (min << 16) | (min << 8) | min;
  const k = min / m;
  return (Math.round(r * k) << 16) | (Math.round(g * k) << 8) | Math.round(b * k);
}
const ofSlot = (v: unknown, slot: CosmeticSlot): v is string => typeof v === 'string' && COSMETIC_DEF[v]?.slot === slot;

/**
 * Apparence valide à partir de données quelconques (client, base) : valeurs inconnues remplacées par celles par défaut,
 * objets non possédés retirés quand `owns` est fourni. Le serveur s'en sert avant d'enregistrer ou de diffuser.
 */
export function sanitizeLook(raw: unknown, owns?: (id: string) => boolean): TankLook {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const ok = (id: string) => FREE_COSMETICS.has(id) || !owns || owns(id);
  const pick = (v: unknown, slot: CosmeticSlot): string | null => (ofSlot(v, slot) && ok(v) ? v : null);
  const partsIn = (o.parts && typeof o.parts === 'object' ? o.parts : {}) as Record<string, Record<string, unknown> | undefined>;
  const parts = {} as Record<PartId, PartPaint>;
  for (const id of PARTS) {
    const p = partsIn[id] ?? {};
    const d = DEFAULT_LOOK.parts[id];
    parts[id] = {
      color: brightEnough(isColor(p.color) ? p.color : d.color, id),
      mat: pick(p.mat, 'mat') ?? d.mat,
      pat: pick(p.pat, 'pat'),
      patColor: isColor(p.patColor) ? p.patColor : d.patColor,
      dec: DECAL_PARTS.includes(id) ? pick(p.dec, 'dec') : null,
      decColor: isColor(p.decColor) ? p.decColor : d.decColor,
    };
  }
  return { parts, aura: pick(o.aura, 'aura'), trail: pick(o.trail, 'trail'), shot: pick(o.shot, 'shot'), kill: pick(o.kill, 'kill'), death: pick(o.death, 'death') };
}

/** Clé stable d'une apparence (cache des images de tank côté client, comparaison côté serveur). */
export function lookKey(l: TankLook): string {
  return JSON.stringify([PARTS.map((p) => [l.parts[p].color, l.parts[p].mat, l.parts[p].pat, l.parts[p].patColor, l.parts[p].dec, l.parts[p].decColor]), l.aura, l.trail, l.shot, l.kill, l.death]);
}

// ─── Coffres et prix (choix de Léni, 4 novembre 2026) ───────────────────

export type ChestId = 'bronze' | 'argent' | 'or';

export interface ChestDef {
  id: ChestId;
  name: string;
  /** Prix en pièces (jamais en gemmes : pas de hasard contre de l'argent réel). */
  coins: number;
  /** Chances par rareté, en pour mille (somme = 1000). */
  odds: Record<Rarity, number>;
}

export const CHESTS: ChestDef[] = [
  { id: 'bronze', name: 'Coffre de bronze', coins: 1000, odds: { commun: 640, rare: 280, epique: 60, legendaire: 18, mythique: 2 } },
  { id: 'argent', name: 'Coffre d’argent', coins: 3000, odds: { commun: 300, rare: 400, epique: 220, legendaire: 70, mythique: 10 } },
  { id: 'or', name: 'Coffre d’or', coins: 6000, odds: { commun: 0, rare: 0, epique: 700, legendaire: 250, mythique: 50 } },
];
export const CHEST_BY_ID: Record<string, ChestDef> = Object.fromEntries(CHESTS.map((c) => [c.id, c]));

/** Pièces rendues pour un objet déjà possédé. */
export const DUPLICATE_REFUND: Record<Rarity, number> = { commun: 300, rare: 600, epique: 1200, legendaire: 2500, mythique: 5000 };

/** Achat direct, sans hasard, en gemmes. */
export const GEM_PRICE: Record<Rarity, number> = { commun: 60, rare: 120, epique: 220, legendaire: 350, mythique: 600 };

/** Objets qu'un coffre peut donner (tout sauf ce qui est gratuit). */
export const CHEST_POOL: CosmeticDef[] = COSMETIC_DEFS.filter((d) => !FREE_COSMETICS.has(d.id));

/** Rareté tirée par un coffre, à partir d'un entier `roll` tiré uniformément dans [0, 1000) par l'appelant. */
export function chestRarity(chest: ChestDef, roll: number): Rarity {
  let acc = 0;
  for (const r of RARITIES) {
    acc += chest.odds[r];
    if (roll < acc) return r;
  }
  throw new Error(`chances du coffre ${chest.id} : total ${acc} ‰ au lieu de 1000`);
}
for (const c of CHESTS) if (RARITIES.reduce((s, r) => s + c.odds[r], 0) !== 1000) throw new Error(`chances du coffre ${c.id} : total différent de 1000 ‰`);

/** Objets d'une rareté que les coffres peuvent donner. */
export function poolOf(rarity: Rarity): CosmeticDef[] {
  return CHEST_POOL.filter((d) => d.rarity === rarity);
}
